import { useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { setWaveEnd } from './motionClock'
import './motion.css'

/** Pixels across the content area (higher = smaller pixels, more elements). */
const PIXEL_AMOUNT = 16
/** How long one pixel takes to fade out, in ms (the website's 0.5s). */
const PIXEL_FADE_MS = 500
/** Pixels start at random within this window, in ms (the website's 0.4s): the whole wave is FADE + SPREAD, 0.9s. */
const PIXEL_SPREAD_MS = 400
/** gsap's power2.inOut, the website's easing. */
const PIXEL_EASING = 'cubic-bezier(0.45, 0, 0.55, 1)'

/**
 * The marketing website's PixelatedWave page transition
 * (Website/fursahub/components/Global/PageTransition/PixelatedWave), enter
 * half only, with the website's 0.9s timing and grey: when the page changes,
 * the content area starts covered by a grid of pixels that dissolve at random.
 *
 * The website's Link fragment also plays a leave animation and holds the
 * navigation until it ends. The portal navigates from antd menus and code
 * (`navigate()`), and an admin moving between pages shouldn't wait, so here
 * the new page renders at once, under the pixels. The rail and top bar stay
 * put; only the content column dissolves. The overlay never takes clicks.
 *
 * Skipped on first load and for reduced motion.
 */
export function PixelatedWave() {
  const { pathname } = useLocation()
  const panelRef = useRef<HTMLDivElement>(null)
  // The page last shown; null before the first render. Comparing pathnames,
  // not a "first run" flag, keeps Strict Mode's re-run from animating.
  const shownPathname = useRef<string | null>(null)

  useLayoutEffect(() => {
    const previous = shownPathname.current
    shownPathname.current = pathname
    const panel = panelRef.current
    if (!panel || previous === null || previous === pathname) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const content = document.querySelector<HTMLElement>('.fh-content')
    if (!content) return
    const rect = content.getBoundingClientRect()
    const top = Math.max(0, rect.top)
    const width = rect.width
    const height = window.innerHeight - top
    if (width <= 0 || height <= 0) return

    panel.style.left = `${rect.left}px`
    panel.style.top = `${top}px`
    panel.style.width = `${width}px`
    panel.style.height = `${height}px`

    // Square pixels: PIXEL_AMOUNT across the longer side, as many as fit across the other.
    const portrait = height > width
    const cell = (portrait ? height : width) / PIXEL_AMOUNT
    const cols = portrait ? Math.ceil(width / cell) : PIXEL_AMOUNT
    const rows = portrait ? PIXEL_AMOUNT : Math.ceil(height / cell)
    panel.style.gridTemplateColumns = `repeat(${cols}, 1fr)`
    panel.style.gridTemplateRows = `repeat(${rows}, ${cell}px)`

    const pixels: HTMLDivElement[] = []
    for (let i = 0; i < cols * rows; i++) {
      const pixel = document.createElement('div')
      pixel.className = 'fh-pixel'
      pixels.push(pixel)
    }
    panel.replaceChildren(...pixels)
    panel.hidden = false
    // PageMotion starts the new page's cards and rows as this clears.
    setWaveEnd(performance.now() + PIXEL_SPREAD_MS + PIXEL_FADE_MS)

    const animations = pixels.map((pixel) =>
      pixel.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: PIXEL_FADE_MS,
        delay: Math.random() * PIXEL_SPREAD_MS,
        easing: PIXEL_EASING,
        fill: 'both',
      }),
    )

    let cancelled = false
    Promise.all(animations.map((a) => a.finished))
      .then(() => {
        if (cancelled) return
        panel.hidden = true
        panel.replaceChildren()
      })
      // A cancelled animation rejects `finished`: the next page's run took over.
      .catch(() => {})

    return () => {
      cancelled = true
      animations.forEach((a) => a.cancel())
      panel.hidden = true
      panel.replaceChildren()
    }
  }, [pathname])

  return <div ref={panelRef} className="fh-pixel-wave" aria-hidden="true" hidden />
}
